import { useState, useCallback } from 'react';
import { toPerMonthRate } from '../../utils/supplyTerms';
import styles from './DosageSelector.module.css';

const DosageSelector = ({
  dosages = [],
  selectedDosage,
  onSelect,
  selectedSupplyMonths = 1,
  tierType = null,
  hasTierPriceModifier = false,
  defaultView = 'expanded',
  className = ''
}) => {
  // Permanent fix: always filter out inactive dosages at component level
  const activeDosages = dosages.filter(d => d.isActive !== false);
  const [view, setView] = useState(defaultView);

  const toggleView = useCallback(() => {
    setView(prev => prev === 'expanded' ? 'minimized' : 'expanded');
  }, []);

  const handleCardSelect = useCallback((dosage) => {
    onSelect?.(dosage);
  }, [onSelect]);

  const handleDropdownChange = useCallback((e) => {
    const dosageId = e.target.value;
    const dosage = activeDosages.find(d => d.id === dosageId);
    onSelect?.(dosage || null);
  }, [dosages, onSelect]);

  const formatPrice = (price) => {
    if (!price && price !== 0) return null;
    // Show the two-decimal remainder only when non-zero (8690 -> "$86.90", 6900 -> "$69")
    // so a modifier-applied effective rate reads exactly like the charge.
    return price % 100 === 0 ? `$${price / 100}` : `$${(price / 100).toFixed(2)}`;
  };

  // A supply rate is either flat cents (legacy) or a tier object {access, one, plus}.
  // The object case used to fail the `> 0` test and silently fall through to the legacy
  // `dosage.price` column, so these cards were tier-blind AND supply-blind.
  const resolveRate = (entry) => {
    if (typeof entry === 'number') return entry > 0 ? entry : null;
    if (entry && typeof entry === 'object') {
      const tier = tierType || 'access';
      return entry[tier] ?? entry.access ?? entry.one ?? entry.plus ?? null;
    }
    return null;
  };

  // Resolve the rate AND the duration key it actually came from, because the divisor below
  // must be the key the money is STORED under — not the one the member selected. When the
  // selected term carries no rate this falls back to key "1", whose internal months are 2,
  // and dividing that by the selected term's months would quote a rate nothing sells at.
  // A null key means the legacy flat `dosage.price` column: it has no supply term, so it is
  // already a per-month rate and must never be divided.
  const resolveRateAndKey = (dosage) => {
    const sp = (hasTierPriceModifier ? dosage.supplyPricingEffective : dosage.supplyPricing)
      ?? dosage.supplyPricing;
    if (sp && typeof sp === 'object') {
      const selectedKey = String(selectedSupplyMonths);
      const selected = resolveRate(sp[selectedKey]);
      if (selected !== null) return { rate: selected, key: selectedKey };
      const base = resolveRate(sp["1"]);
      if (base !== null) return { rate: base, key: "1" };
    }
    return { rate: dosage.price, key: null };
  };

  // Get effective monthly price considering supply duration. WHICH server map is charge-exact
  // depends on the VIEWER, not on the tier label: `pricing.tierType` collapses "Oval Access
  // member" (product_price_modifier +0.10) and "no membership at all" (no modifier) onto the same
  // key `access`, and the effective map is built from the viewer-agnostic per-tier modifier map,
  // so it carries Access's +10% even for a signed-out visitor. Reading it unconditionally quoted
  // that visitor $86.90/mo while pricingEngine charged $79/mo (it reads RAW supplyPricing only).
  // The caller supplies the discriminator from the BACKEND; it defaults to false because raw is
  // what a signed-out visitor is charged. No modifier math in the browser -- this only PICKS a map.
  // The rate is quoted per INTERNAL month so the 60 Day term stops advertising two months
  // of medication at its two-month total — a no-op for keys "3"/"6"/"12".
  const getEffectivePrice = (dosage) => {
    if (!dosage) return null;
    const { rate, key } = resolveRateAndKey(dosage);
    return key === null ? rate : toPerMonthRate(rate, key);
  };

  const formatDosageLabel = (dosage) => {
    if (!dosage) return '';
    return dosage.description
      ? `${dosage.amount} - ${dosage.description}`
      : dosage.amount;
  };

  if (!dosages || activeDosages.length === 0) {
    return null;
  }

  return (
    <div className={`${styles.container} ${className}`}>
      <div className={styles.header}>
        <span className={styles.label}>Select dosage</span>
        <button
          type="button"
          className={styles.toggleButton}
          onClick={toggleView}
          aria-label={view === 'expanded' ? 'Switch to dropdown view' : 'Switch to card view'}
        >
          {view === 'expanded' ? (
            <>
              <svg
                className={styles.toggleIcon}
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
              <span className={styles.toggleText}>Dropdown</span>
            </>
          ) : (
            <>
              <svg
                className={styles.toggleIcon}
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="3" width="7" height="7" />
                <rect x="14" y="3" width="7" height="7" />
                <rect x="14" y="14" width="7" height="7" />
                <rect x="3" y="14" width="7" height="7" />
              </svg>
              <span className={styles.toggleText}>Cards</span>
            </>
          )}
        </button>
      </div>

      {view === 'expanded' ? (
        <div className={styles.cardGrid}>
          {activeDosages.map(dosage => {
            const isSelected = selectedDosage?.id === dosage.id;
            const effectivePrice = getEffectivePrice(dosage);
            const priceDisplay = formatPrice(effectivePrice);

            return (
              <button
                key={dosage.id}
                type="button"
                className={`${styles.dosageCard} ${isSelected ? styles.selected : ''}`}
                onClick={() => handleCardSelect(dosage)}
                aria-pressed={isSelected}
              >
                <div className={styles.cardContent}>
                  <span className={styles.dosageAmount}>{dosage.amount}</span>
                  {dosage.description && (
                    <span className={styles.dosageDescription}>{dosage.description}</span>
                  )}
                </div>
                {priceDisplay && (
                  <span className={styles.dosagePrice}>{priceDisplay}/mo</span>
                )}
                {isSelected && (
                  <div className={styles.selectedIndicator}>
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      ) : (
        <div className={styles.dropdownWrapper}>
          <select
            className={styles.dropdown}
            value={selectedDosage?.id || ''}
            onChange={handleDropdownChange}
            aria-label="Select dosage"
          >
            <option value="" disabled>
              Select a dosage
            </option>
            {activeDosages.map(dosage => {
              const effectivePrice = getEffectivePrice(dosage);
              return (
                <option key={dosage.id} value={dosage.id}>
                  {formatDosageLabel(dosage)}
                  {effectivePrice ? ` - ${formatPrice(effectivePrice)}/mo` : ''}
                </option>
              );
            })}
          </select>
          <svg
            className={styles.dropdownArrow}
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </div>
      )}
    </div>
  );
};

export default DosageSelector;
